import type { AdminModulesResponse, AdminClassResponse, AdminSubjectsResponse, AdminUsersResponse, AdminConversationResponse, AdminConversationDetailResponse, ToggleCorrectnessRequest, UpdateMessageScoreRequest, UpdateMessageScoreResponse, AdminDayResponse, AdminDayConversationResponse, UpdateSubjectRequest, CreateModuleRequest, UpdateModuleRequest, CreateClassRequest, UpdateClassRequest, UpdateClassModuleAssignmentRequest, ReorderSubjectsRequest, SubjectDocumentDto } from '@/types/admin';
import { API_BASE_URL } from '@/lib/config';
import { authFetch } from '@/lib/authFetch';

export const adminService = {
  async getAllModules(): Promise<AdminModulesResponse[]> {
    const token = localStorage.getItem('token'); 
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/modules`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch all modules');
    }

    return response.json();
  },

  async deleteModule(moduleId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/modules/${moduleId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to delete module');
    }
  },

  async updateModule(moduleId: string, request: UpdateModuleRequest): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/modules/${moduleId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to update module');
    }
  },

  async createModule(request: CreateModuleRequest): Promise<AdminModulesResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/modules`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to create module');
    }

    return response.json();
  },

  async getAllClasses(): Promise<AdminClassResponse[]> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch all classes');
    }

    return response.json();
  },

  async getClassesByModule(moduleId: string): Promise<AdminClassResponse[]> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${moduleId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to fetch classes for module');
    }

    return response.json();
  },

  async getAvailableClasses(moduleId: string, search: string): Promise<AdminClassResponse[]> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const query = new URLSearchParams();
    if (search.trim()) query.set('search', search.trim());
    const response = await authFetch(`${API_BASE_URL}/admin/classes/${moduleId}/available?${query}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to search available classes');
    }

    return response.json();
  },

  async createClass(moduleId: string, request: CreateClassRequest): Promise<AdminClassResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${moduleId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to create class');
    }

    return response.json();
  },

  async createClassStandalone(request: CreateClassRequest): Promise<AdminClassResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to create class');
    }

    return response.json();
  },

  async updateClass(classId: string, request: UpdateClassRequest): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to update class');
    }
  },

  async updateClassModuleAssignment(classId: string, request: UpdateClassModuleAssignmentRequest): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}/modules/${request.moduleId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to update class module assignment');
    }
  },

  async generateSebConfigFile(classId: string): Promise<{ blob: Blob; configKey: string | null; fileName: string }> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}/seb-config-file`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'SEB-Konfigurationsdatei konnte nicht generiert werden');
    }

    const configKey = response.headers.get('X-Seb-Config-Key');
    const disposition = response.headers.get('Content-Disposition');
    const fileNameMatch = disposition?.match(/filename="?([^";]+)"?/);
    const fileName = fileNameMatch?.[1] ?? 'config.seb';
    const blob = await response.blob();
    return { blob, configKey, fileName };
  },

  async addClassToModule(classId: string, moduleId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}/modules/${moduleId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to add class to module');
    }
  },

  async removeClassFromModule(classId: string, moduleId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}/modules/${moduleId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to remove class from module');
    }
  },

  async deleteClass(classId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to delete class');
    }
  },

  async getSubjectsByModule(moduleId: string): Promise<AdminSubjectsResponse[]> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/subjects/${moduleId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch subjects for module');
    }

    return response.json();
  },

  async createSubject(moduleId: string, request: UpdateSubjectRequest): Promise<AdminSubjectsResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/subjects/${moduleId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to create subject');
    }

    return response.json();
  },

  async updateSubject(subjectId: string, request: UpdateSubjectRequest): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/subject/${subjectId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to update subject');
    }
  },

  async duplicateSubject(subjectId: string): Promise<AdminSubjectsResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/subject/${subjectId}/duplicate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to duplicate subject');
    }

    return response.json();
  },

  async deleteSubject(subjectId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/subject/${subjectId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to delete subject');
    }
  },

  async reorderSubjects(moduleId: string, request: ReorderSubjectsRequest): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/subjects/${moduleId}/order`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to reorder subjects');
    }
  },

  async getUsersByModule(moduleId: string): Promise<AdminUsersResponse[]> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/users/${moduleId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users for module');
    }

    return response.json();
  },

  async getUsersByClass(classId: string): Promise<AdminUsersResponse[]> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}/users`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to fetch users for class');
    }

    return response.json();
  },

  async importUsersToClass(classId: string, emails: string[]): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}/users/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ emails }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to import users to class');
    }
  },

  async deactivateUserInClass(classId: string, userId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to remove user from class');
    }
  },

  async reactivateUserInClass(classId: string, userId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}/users/${userId}/reactivate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to reactivate user in class');
    }
  },

  async resendInvitation(classId: string, email: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/classes/${classId}/users/resend-invitation`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to resend invitation');
    }
  },

  async getConversationsByUser(userId: string, classId?: string): Promise<AdminConversationResponse[]> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const query = new URLSearchParams();
    if (classId) query.set('classId', classId);
    const response = await authFetch(`${API_BASE_URL}/admin/conversations/${userId}?${query}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch conversations for user');
    }

    return response.json();
  },

  async getConversationById(conversationId: string): Promise<AdminConversationDetailResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/conversation/${conversationId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch conversation details');
    }

    return response.json();
  },

  async reopenConversation(conversationId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/conversation/${conversationId}/reopen`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to reopen conversation');
    }
  },

  async deleteConversation(conversationId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/conversation/${conversationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to delete conversation');
    }
  },

  async getDaysByModule(moduleId: string, classId: string): Promise<AdminDayResponse[]> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/days/${moduleId}?classId=${classId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch days for module');
    }

    return response.json();
  },

  async getConversationsByModuleAndDate(moduleId: string, date: string, classId: string): Promise<AdminDayConversationResponse[]> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/days/${moduleId}/${date}?classId=${classId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch conversations for date');
    }

    return response.json();
  },

  async toggleDiscussed(conversationId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/conversation/${conversationId}/toggle-discussed`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to toggle discussed status');
    }
  },

  async toggleAdjusted(conversationId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/conversation/${conversationId}/toggle-adjusted`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to toggle adjusted status');
    }
  },

  async toggleCorrectness(request: ToggleCorrectnessRequest): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/conversation/toggle-correctness`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to toggle correctness');
    }
  },

  async updateMessageScore(request: UpdateMessageScoreRequest): Promise<UpdateMessageScoreResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/conversation/message/score`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Failed to update message score');
    }

    return response.json();
  },

  async uploadSubjectDocument(subjectId: string, file: File): Promise<SubjectDocumentDto> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await authFetch(`${API_BASE_URL}/admin/subject/${subjectId}/documents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Dokument konnte nicht hochgeladen werden');
    }

    return response.json();
  },

  async deleteSubjectDocument(subjectId: string, documentId: string): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await authFetch(`${API_BASE_URL}/admin/subject/${subjectId}/documents/${documentId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || 'Document could not be deleted');
    }
  },

  getSubjectDocumentDownloadUrl(subjectId: string, documentId: string): string {
    return `${API_BASE_URL}/admin/subject/${subjectId}/documents/${documentId}`;
  },

  async getModuleNameById(moduleId: string): Promise<string> {
    const modules = await this.getAllModules();
    const mod = modules.find(m => m.id === moduleId);
    return mod?.name ?? '';
  },

  async getClassNameById(classId: string): Promise<string> {
    const classes = await this.getAllClasses();
    const cls = classes.find(c => c.id === classId);
    return cls?.name ?? '';
  },
};
